import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/videos';

export default function AboutPage() {
  const pageUrl = `${siteInfo.url}/about`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、平台优势和联系方式。${siteInfo.description}`,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100/50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-900 font-medium">关于我们</span>
              </nav>
            </div>
          </div>

          <section className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100">
                  <i className="ri-movie-2-line text-3xl text-primary-600"></i>
                </div>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于 {siteInfo.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {siteInfo.description}
              </p>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                品牌故事
              </h2>
              <div className="space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  桃花视频成立于2024年，致力于为广大影视爱好者打造一个集红桃视频、樱桃视频、茄子视频、糖心视频等多平台资源于一体的高清视频聚合平台。我们深知，在这个信息爆炸的时代，观众面临着资源分散、画质参差不齐、播放卡顿等诸多困扰。
                </p>
                <p>
                  正是基于这样的洞察，桃花视频应运而生。我们通过先进的多源聚合技术，将分散于各大平台的优质视频资源汇聚一处。无论是热门电视剧、最新院线电影、精彩综艺节目，还是优质动漫和纪录片，用户都能在桃花视频一站找到并流畅观看。
                </p>
                <p>
                  桃花视频始终秉持"让每个人都能轻松享受高清影视"的使命。我们不断优化技术架构，采用CDN加速和多线路智能切换机制，确保用户在观看视频时享受极致流畅的体验。同时，我们的内容团队每日实时跟进最新资源更新，确保您不会错过任何一部好剧。
                </p>
                <p>
                  我们的团队由一群热爱影视、精通技术的年轻人组成。我们相信，技术应该服务于内容，让好故事触手可及。未来，桃花视频将继续拓展合作伙伴网络，不断丰富资源库，提升服务质量，努力成为中国领先的视频聚合平台。
                </p>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '影迷小王',
                    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20portrait%20avatar%2C%20casual%20style%2C%20friendly%20smile%2C%20clean%20background%2C%20professional%20headshot&width=80&height=80&seq=taohua-avatar-001&orientation=squarish',
                    text: '桃花视频太棒了！资源真的很全，我想看的电视剧都能找到，而且画质清晰，播放流畅不卡顿。现在追剧全靠它了！',
                    rating: 5,
                  },
                  {
                    name: '追剧达人',
                    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20portrait%20avatar%2C%20modern%20fashion%20style%2C%20warm%20smile%2C%20clean%20minimal%20background%2C%20professional%20headshot&width=80&height=80&seq=taohua-avatar-002&orientation=squarish',
                    text: '之前为了追一部剧要到处找资源，现在桃花视频一个平台就搞定了。红桃视频、樱桃视频的资源这里都有，太方便了。',
                    rating: 5,
                  },
                  {
                    name: '电影爱好者',
                    avatar: 'https://readdy.ai/api/search-image?query=Middle-aged%20Chinese%20man%20portrait%20avatar%2C%20casual%20professional%20look%2C%20gentle%20smile%2C%20clean%20simple%20background%2C%20professional%20headshot&width=80&height=80&seq=taohua-avatar-003&orientation=squarish',
                    text: '更新速度真的快！新上映的电影基本上隔天就能看到，而且免费不需要付费会员，推荐给了很多朋友。',
                    rating: 5,
                  },
                ].map((review) => (
                  <div key={review.name} className="p-5 md:p-6 rounded-lg bg-background-100/50 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <h4 className="font-heading font-semibold text-foreground-900 text-sm">{review.name}</h4>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <div key={i} className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-xs text-accent-500"></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-mail-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">商务合作</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您希望与我们建立合作关系，包括广告投放、内容合作、品牌联动等，欢迎通过邮件与我们联系。
                  </p>
                  <p className="text-sm text-primary-600 mt-3 font-medium">business@taohuavideo.com</p>
                </div>

                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-customer-service-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">用户反馈</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您在使用过程中遇到任何问题，或有任何建议和意见，欢迎随时通过邮件反馈给我们。我们会认真对待每一条反馈。
                  </p>
                  <p className="text-sm text-accent-600 mt-3 font-medium">support@taohuavideo.com</p>
                </div>

                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                    <i className="ri-shake-hands-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">版权声明</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    桃花视频聚合的内容均来自公开网络资源，如涉及版权问题，请版权方联系我们的版权团队，我们将及时处理。
                  </p>
                  <p className="text-sm text-secondary-600 mt-3 font-medium">copyright@taohuavideo.com</p>
                </div>

                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-time-line text-xl text-foreground-600"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">工作时间</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的客服团队工作时间为周一至周五 9:00-18:00（法定节假日除外）。
                    在工作时间内，我们会在24小时内回复您的邮件。
                  </p>
                  <p className="text-sm text-foreground-500 mt-3">周一至周五 9:00 - 18:00</p>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                加入我们的旅程
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-6 leading-relaxed">
                桃花视频正在快速成长，我们欢迎每一位热爱影视的朋友加入我们的社区。
                如果您有任何问题、建议或合作意向，请随时联系我们。
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页开始观影
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </section>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  { '@type': 'Question', name: '桃花视频是如何聚合多平台资源的？', acceptedAnswer: { '@type': 'Answer', text: '桃花视频采用先进的多源聚合技术，将红桃视频、樱桃视频、茄子视频、糖心视频等平台的优质视频资源汇聚一处，用户无需切换多个平台即可浏览和观看所有内容。' } },
                  { '@type': 'Question', name: '桃花视频是否支持手机观看？', acceptedAnswer: { '@type': 'Answer', text: '是的，桃花视频全面支持PC端、平板和手机浏览器访问，页面自适应不同屏幕尺寸，无论您使用什么设备都能获得舒适的观影体验。' } },
                  { '@type': 'Question', name: '如何联系桃花视频进行商务合作？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过发送邮件至 business@taohuavideo.com 与我们联系商务合作事宜，我们的团队会在工作时间内尽快回复。' } },
                ],
              }),
            }}
          />
        </main>

        <Footer />
      </div>
    </>
  );
}